import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PlaybackModal from "./components/PlaybackModal";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatViews, getMediaById, getRelated } from "@/lib/media";

export default function Detail() {
  const { id } = useParams();
  const media = id ? getMediaById(id) : undefined;

  const { isFavorite, toggleFavorite } = useFavorites();
  const [playOpen, setPlayOpen] = useState(false);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: media
      ? `${media.title} - 详细介绍 - 免费观看`
      : "影片详情 - 免费观看大片在线观看",
    description: media
      ? `${media.title}（${media.year}）${media.genres.join("、")}影片详细介绍，${media.synopsis.slice(0, 60)}…`
      : "影片详细介绍页面。",
    keywords: media
      ? `${media.title},${media.genres.join(",")},影片详情,免费观看大片在线观看`
      : "影片详情",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!media) {
    return (
      <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-foreground-500">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
          没有找到这部影片
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          该影片可能已下线或链接地址有误，返回列表继续浏览吧。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/movies"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            浏览电影
          </Link>
          <Link
            to="/"
            className="rounded-md border border-background-200/70 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(media, 3);
  const favorited = isFavorite(media.id);
  const backTo = media.kind === "movie" ? "/movies" : "/tvshows";
  const backLabel = media.kind === "movie" ? "电影" : "电视剧";

  const handleFavorite = () => {
    const nowFavorited = toggleFavorite(media.id);
    setToast(nowFavorited ? "已收藏，可在本机查看" : "已取消收藏");
  };

  const infoRows = [
    { label: "类型", value: media.genres.join(" / ") },
    { label: "导演", value: media.director },
    { label: "主演", value: media.cast.join(" / ") },
    { label: "地区", value: media.region },
    { label: "上映", value: `${media.year} 年` },
    media.kind === "movie"
      ? { label: "片长", value: media.duration || "—" }
      : { label: "集数", value: media.episodes ? `全 ${media.episodes} 集` : "—" },
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-500">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={backTo} className="transition-colors hover:text-primary-500">
          {backLabel}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{media.title}</span>
      </nav>

      {/* Hero */}
      <section className="relative mt-4 overflow-hidden rounded-lg border border-background-200/60">
        <div className="absolute inset-0">
          <img
            src={media.backdrop}
            alt={`${media.title} 背景`}
            className="h-full w-full scale-110 object-cover object-center opacity-30 blur-md"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/40"></div>
        </div>

        <div className="relative flex flex-col gap-6 p-5 sm:flex-row md:p-8">
          <div className="w-40 shrink-0 overflow-hidden rounded-lg border border-background-200/60 sm:w-48 md:w-56">
            <img
              src={media.poster}
              alt={`${media.title} 海报`}
              title={`${media.title} 在线观看`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                {media.kind === "movie" ? "电影" : "电视剧"}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-accent-100/80 px-2.5 py-1 text-xs font-semibold text-accent-900">
                <i className="ri-star-fill text-[11px]"></i>
                {media.rating.toFixed(1)} 分
              </span>
              {media.status && (
                <span className="rounded-md bg-secondary-100/80 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {media.status}
                </span>
              )}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              {media.title}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">{media.titleEn}</p>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
              <span>{media.genres.join(" · ")}</span>
              <span className="text-background-400">|</span>
              <span>{media.region}</span>
              <span className="text-background-400">|</span>
              <span>{media.year}</span>
              <span className="text-background-400">|</span>
              <span>{media.kind === "movie" ? media.duration : `全 ${media.episodes} 集`}</span>
            </div>

            <p className="mt-4 flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-fire-line text-primary-500"></i>
              {formatViews(media.views)} 热度 · 观众正在关注
            </p>

            <div className="mt-auto flex flex-wrap items-center gap-3 pt-6">
              <button
                type="button"
                onClick={() => setPlayOpen(true)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                在线观看入口
              </button>
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-medium transition-colors ${
                  favorited
                    ? "border-primary-400 bg-primary-100/70 text-primary-700"
                    : "border-background-200/70 text-foreground-700 hover:border-primary-400 hover:text-primary-500"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <Link
                to={backTo}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200/70 px-5 py-3 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-500"
              >
                <i className="ri-arrow-left-line"></i>
                返回{backLabel}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
        <div className="lg:col-span-2">
          {/* Basic info */}
          <section className="rounded-lg border border-background-200/60 bg-background-100/50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-primary-500"></span>
              影片信息
            </h2>
            <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <dt className="w-14 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Synopsis */}
          <section className="mt-6 rounded-lg border border-background-200/60 bg-background-100/50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-primary-500"></span>
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              {media.synopsis}
            </p>
          </section>

          {/* Resources */}
          <section className="mt-6 rounded-lg border border-background-200/60 bg-background-100/50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-primary-500"></span>
              在线观看入口
            </h2>
            <p className="mt-3 flex items-start gap-2 rounded-md border border-accent-300/40 bg-accent-100/40 p-3 text-xs leading-relaxed text-accent-900">
              <i className="ri-information-line mt-0.5 text-base"></i>
              以下入口均为演示，点击后仅弹出说明层，本站不提供真实播放、下载或会员服务。
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { name: "高清线路一", tag: "演示" },
                { name: "高清线路二", tag: "演示" },
              ].map((line) => (
                <button
                  key={line.name}
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="group flex items-center justify-between rounded-md border border-background-200/70 bg-background-50 px-4 py-3.5 text-left transition-colors hover:border-primary-400"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100/70 text-primary-600">
                      <i className="ri-play-fill text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-500">
                        {line.name}
                      </span>
                      <span className="mt-0.5 block text-xs text-foreground-500">
                        点击查看播放演示
                      </span>
                    </span>
                  </span>
                  <span className="rounded-full bg-secondary-100/80 px-2.5 py-1 text-[11px] text-secondary-900">
                    {line.tag}
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>

        {/* Related */}
        <aside className="lg:col-span-1">
          <section className="rounded-lg border border-background-200/60 bg-background-100/50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-magic-line text-primary-500"></i>
              相关推荐
            </h2>
            <p className="mt-1.5 text-xs text-foreground-500">根据类型相似度为你精选</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-1">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex gap-3 rounded-md p-2 transition-colors hover:bg-background-200/50"
                >
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={item.poster}
                      alt={`${item.title} 海报`}
                      title={`${item.title} 在线观看`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-500">
                      {item.title}
                    </p>
                    <p className="mt-1 truncate text-xs text-foreground-500">
                      {item.genres.join(" · ")}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">
                      {item.year} · {item.region}
                    </p>
                    <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-accent-400">
                      <i className="ri-star-fill text-[10px]"></i>
                      {item.rating.toFixed(1)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <PlaybackModal media={media} open={playOpen} onClose={() => setPlayOpen(false)} />

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[110] -translate-x-1/2 animate-fade-up rounded-full border border-background-200/60 bg-background-100 px-5 py-2.5 text-sm text-foreground-900 shadow-lg">
          <span className="flex items-center gap-2">
            <i className="ri-heart-3-fill text-primary-500"></i>
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}